import { useState, type ReactNode } from 'react';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';
import {
  budgetControlStatus,
  type WorkOrderBudgetStatus,
} from '../../api/brandingWorkOrderApi.js';

/**
 * What a work order's budget position looks like on screen, wherever the order
 * was saved or sent for approval from. Issuing an order and submitting a saved
 * draft are the same act at different moments, so they read the server's
 * answer through this one piece and can never word it differently.
 */

/**
 * Said once an order has gone for approval. Who approves is the tenant's own
 * workflow, so the role is not named here; the list's "Waiting on" shows it.
 */
export function workOrderSubmittedMessage(woNumber: string): string {
  return `${woNumber} submitted for approval`;
}

type Money = (value: number | null | undefined) => string;
type Num = (value: number | null | undefined, fractionDigits?: number) => string;

/**
 * The budget position in words. A BLOCK (EX-WO-16) states the figure the
 * tenant's BUDGET_CONTROL_POLICY judged, with the tolerance when the policy
 * allowed one; anything else repeats the server's own warning against the
 * matched drawing. Amounts are the server's, formatted, never recomputed.
 */
export function workOrderBudgetWarning(
  status: WorkOrderBudgetStatus | null,
  money: Money,
  number: Num,
): string | null {
  if (!status) return null;
  const projected = status.decision === 'BLOCK'
    ? status.decisionProjectedAmount
    : status.projectedAmount;
  if (status.decision === 'BLOCK') {
    return status.outcome === 'BUDGET_EXCEEDED' && projected !== undefined
      ? `Projected committed value ${money(projected)} against a budget of ${money(status.budgetAmount)}${status.policy === 'ALLOW_WITHIN_TOLERANCE' ? `; configured tolerance ${number(status.tolerancePct)}%` : ''}.`
      : null;
  }
  if (!status.warning) return null;
  return projected === undefined
    ? status.warning
    : `This work order takes committed value to ${money(projected)} against a budget of ${money(status.budgetAmount)} drawn for this combination. ${status.warning}`;
}

export interface WorkOrderBudget {
  readonly warning: string | null;
  /** The position the server returned with a saved or submitted order. */
  readonly record: (status: WorkOrderBudgetStatus | null | undefined) => void;
  /** A refusal carries the budget decision only when that is why it was refused. */
  readonly recordFailure: (error: unknown) => void;
  readonly clear: () => void;
}

export function useWorkOrderBudget(): WorkOrderBudget {
  const { money, number } = useBusinessNumber();
  const [status, setStatus] = useState<WorkOrderBudgetStatus | null>(null);
  return {
    warning: workOrderBudgetWarning(status, money, number),
    record: (s) => setStatus(s ?? null),
    recordFailure: (e) => setStatus(budgetControlStatus(e)),
    clear: () => setStatus(null),
  };
}

/** Advisory, so it is a status and not an alert; the error carries the refusal. */
export function WorkOrderBudgetWarning({ warning }: { readonly warning: string | null }): ReactNode {
  return warning ? <p role="status" data-testid="budget-warning">{warning}</p> : null;
}
